import React, { useState, useMemo, useCallback } from 'react';
import { X, ChevronDown } from 'lucide-react';
import http from '../services/httpClient';
import './SwiggyOrderReportModal.css';

// YYYY-MM-DD -> DD-MM-YYYY for display
function fmtDMY(d) {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(d);
}

// Inline row for manually completing a truncated order (menu picker + qty + price)
function ManualAddRow({ menuList, onAdd }) {
  const [name, setName] = useState('');
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState('');
  const submit = () => {
    if (!name.trim()) return;
    onAdd({ item_name: name.trim(), quantity: qty, price });
    setName(''); setQty(1); setPrice('');
  };
  return (
    <div className="sorm-manual-row">
      <input
        list="sorm-menu-list"
        placeholder="Add missing item…"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          const m = menuList.find(x => (x.name || '').toLowerCase() === e.target.value.toLowerCase());
          if (m) setPrice(m.price);
        }}
        className="sorm-manual-input"
        style={{ flex: 2 }}
      />
      <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="sorm-manual-input" style={{ width: 48 }} title="Qty" />
      <input type="number" placeholder="₹" value={price} onChange={(e) => setPrice(e.target.value)} className="sorm-manual-input" style={{ width: 68 }} title="Price" />
      <button type="button" className="sorm-manual-add" onClick={submit}>Add</button>
    </div>
  );
}

/**
 * Shared review + import modal for a parsed Swiggy Order Report.
 *
 * Props:
 *  - orderReport: the /online-sales/upload response ({ orders, dateSummary, summary, matchSummary })
 *  - menuList: [{ menu_item_id, name, price }] for manual completion of truncated orders
 *  - onClose(): close the modal
 *  - onImported(resultData): called after a successful bulk import
 */
export default function SwiggyOrderReportModal({ orderReport, menuList = [], onClose, onImported, onEditRecipe }) {
  const platform = (orderReport?.platform || 'swiggy').toLowerCase(); // 'swiggy' | 'zomato'
  const platformLabel = platform === 'zomato' ? 'Zomato Order Report' : 'Swiggy Order Report';
  const platformColor = platform === 'zomato' ? '#e23744' : '#ea580c';

  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false); // stock pre-check in progress
  const [results, setResults] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [manualAdds, setManualAdds] = useState({});
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [error, setError] = useState(null);
  const [priceOverrides, setPriceOverrides] = useState({}); // { 'orderIdx:itemIdx': editedPrice }
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  // Missing-ingredients / negative-stock confirmation
  const [showMissing, setShowMissing] = useState(false);
  const [missingIngredients, setMissingIngredients] = useState([]);
  const [miSelected, setMiSelected] = useState({});
  const [miSearch, setMiSearch] = useState('');
  const miKey = (ing) => `${ing.itemName}|${ing.ingredientName}`;

  const priceFor = (idx, ii, item) => {
    const k = `${idx}:${ii}`;
    return priceOverrides[k] !== undefined && priceOverrides[k] !== '' ? Number(priceOverrides[k]) : (item.true_selling_price || 0);
  };

  // Distinct unmatched items (imported as Not in Menu lines) with total quantity
  const unmatchedList = useMemo(() => {
    const m = new Map();
    (orderReport?.orders || []).forEach(o => {
      if (o.is_cancelled) return;
      o.items.forEach(it => {
        if (!it.is_matched) {
          if (!m.has(it.item_name)) m.set(it.item_name, { name: it.item_name, qty: 0 });
          m.get(it.item_name).qty += it.quantity;
        }
      });
    });
    return [...m.values()].sort((a, b) => b.qty - a.qty);
  }, [orderReport]);

  // Cancelled orders (excluded from revenue + deduction)
  const cancelledOrders = useMemo(
    () => (orderReport?.orders || []).filter(o => o.is_cancelled),
    [orderReport]
  );

  const buildDates = useCallback(() => {
    if (!orderReport) return [];
    const importedDates = new Set(
      (orderReport.dateSummary || []).filter(d => d.already_imported).map(d => d.order_date)
    );
    const byDate = new Map();

    orderReport.orders.forEach((o, idx) => {
      if (o.is_cancelled || !o.order_date || importedDates.has(o.order_date)) return;
      const collected = [];
      // Lines not on the menu go in as Not in Menu lines of their date, money kept
      // (spec 2026-10-26-tsr-add-as-not-in-menu) — never dropped.
      const notInMenu = [];
      o.items.forEach((it, ii) => {
        if (!it.is_matched) {
          const price = priceFor(idx, ii, it);
          notInMenu.push({ itemName: it.item_name, quantity: Number(it.quantity) || 0, amount: price * (Number(it.quantity) || 0) });
          return;
        }
        if (it.is_matched && it.mapped_menu_item_id) {
          const price = priceFor(idx, ii, it);
          collected.push({
            menu_item_id: it.mapped_menu_item_id,
            item_name: it.matched_name || it.item_name,
            quantity: it.quantity,
            unit_price: price,
            amount: price * it.quantity,
            platform_original_price: it.platform_original_price || 0,
            platform_discount: it.platform_discount || 0,
            platform_commission: 0,
            true_selling_price: price,
          });
        }
      });
      (manualAdds[idx] || []).forEach(m => {
        if (!m.mapped_menu_item_id || !m.quantity || !m.price) return;
        collected.push({
          menu_item_id: m.mapped_menu_item_id,
          item_name: m.item_name,
          quantity: Number(m.quantity),
          unit_price: Number(m.price),
          amount: Number(m.price) * Number(m.quantity),
          platform_original_price: Number(m.price) * Number(m.quantity),
          platform_discount: 0,
          platform_commission: 0,
          true_selling_price: Number(m.price),
        });
      });
      if (!collected.length && !notInMenu.length) return;
      if (!byDate.has(o.order_date)) byDate.set(o.order_date, { items: [], notInMenu: [] });
      byDate.get(o.order_date).items.push(...collected);
      byDate.get(o.order_date).notInMenu.push(...notInMenu);
    });

    const round2 = (n) => Math.round(n * 100) / 100;
    const dates = [];
    for (const [order_date, { items, notInMenu }] of byDate) {
      // One Not in Menu line per name and date: plates and money summed.
      const nim = new Map();
      notInMenu.forEach(l => {
        const e = nim.get(l.itemName) || { itemName: l.itemName, quantity: 0, amount: 0 };
        e.quantity += l.quantity;
        e.amount += l.amount;
        nim.set(l.itemName, e);
      });
      const unmappedItems = [...nim.values()].map(e => ({
        itemName: e.itemName,
        quantity: e.quantity,
        unitPrice: e.quantity > 0 ? round2(e.amount / e.quantity) : 0,
        amount: round2(e.amount),
        reason: 'not_in_menu',
      }));
      const agg = new Map();
      items.forEach(it => {
        if (!agg.has(it.menu_item_id)) { agg.set(it.menu_item_id, { ...it }); }
        else {
          const e = agg.get(it.menu_item_id);
          e.quantity += it.quantity;
          e.amount += it.amount;
          e.platform_original_price += it.platform_original_price;
          e.platform_discount += it.platform_discount;
        }
      });
      dates.push({ order_date, salesItems: [...agg.values()], unmappedItems });
    }
    return dates.sort((a, b) => (a.order_date < b.order_date ? -1 : 1));
  }, [orderReport, manualAdds, priceOverrides]);

  const newDatesCount = useMemo(
    () => (orderReport?.dateSummary || []).filter(d => !d.already_imported).length,
    [orderReport]
  );

  const addManualItem = useCallback((orderIdx, entry) => {
    const match = menuList.find(m => (m.name || '').toLowerCase() === (entry.item_name || '').toLowerCase());
    if (!match) { setError(`"${entry.item_name}" is not in the menu — pick a listed item.`); return; }
    setError(null);
    setManualAdds(prev => ({
      ...prev,
      [orderIdx]: [...(prev[orderIdx] || []), {
        item_name: match.name,
        mapped_menu_item_id: match.menu_item_id,
        db_price: Number(match.price) || 0,
        quantity: Number(entry.quantity) || 1,
        price: Number(entry.price) || Number(match.price) || 0,
      }]
    }));
  }, [menuList]);

  const removeManualItem = useCallback((orderIdx, i) => {
    setManualAdds(prev => ({
      ...prev,
      [orderIdx]: (prev[orderIdx] || []).filter((_, idx) => idx !== i)
    }));
  }, []);

  // Actual multi-date import (deducts stock). allowNeg=true creates negative stock.
  const doImport = useCallback(async (allowNeg) => {
    const dates = buildDates();
    if (!dates.length) {
      setError('No new dates to import (all already imported or no matched items).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await http.post('/online-sales/submit-order-report', {
        dates,
        platformSource: platform,
        allowNegativeStock: allowNeg,
      });
      const data = resp.data || resp;
      if (data.success) {
        setResults(data);
        if (onImported) onImported(data);
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError(err.data?.error || err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }, [buildDates, platform, onImported]);

  // Confirm: run a stock pre-check FIRST. If any ingredient is short, show the
  // Missing Ingredients popup and DO NOT proceed — the sale is only committed
  // after the user clicks "Confirm & Create Negative Stock". Runs regardless of
  // the "Allow negative stock" checkbox.
  const handleSubmit = useCallback(async () => {
    const dates = buildDates();
    if (!dates.length) {
      setError('No new dates to import (all already imported or no matched items).');
      return;
    }

    // Aggregate every item across all new dates → [{ menuItemId, quantity }]
    const agg = new Map();
    dates.forEach(d => d.salesItems.forEach(it => {
      agg.set(it.menu_item_id, (agg.get(it.menu_item_id) || 0) + it.quantity);
    }));
    const preItems = [...agg.entries()].map(([menuItemId, quantity]) => ({ menuItemId, quantity }));

    setChecking(true);
    setError(null);
    try {
      const resp = await http.post('/total-sales/sales/pre-check-stock', { items: preItems });
      const data = resp.data || resp;
      const missing = (data && data.missingIngredients) || [];
      if ((data && data.hasNegativeStock) && missing.length > 0) {
        setMissingIngredients(missing);
        const sel = {};
        missing.forEach(ing => { sel[miKey(ing)] = true; });
        setMiSelected(sel);
        setMiSearch('');
        setShowMissing(true);   // gate: do NOT proceed until user confirms
        setChecking(false);
        return;
      }
    } catch (err) {
      // Pre-check failed — don't hard-block; fall through to a normal import
      console.warn('Stock pre-check failed, proceeding to import:', err?.message || err);
    }
    setChecking(false);
    await doImport(false);
  }, [buildDates, doImport]);

  // "Confirm & Create Negative Stock" — only now does the sale proceed (negative stock)
  const handleConfirmNegativeStock = useCallback(async () => {
    setShowMissing(false);
    await doImport(true);
  }, [doImport]);

  // "Remove and Update Recipe" — navigate to the recipe editor for an affected item
  const handleRemoveFromRecipe = useCallback(() => {
    const selected = missingIngredients.filter(ing => miSelected[miKey(ing)]);
    const first = selected[0] || missingIngredients[0];
    const menuItem = first && menuList.find(m => (m.name || '').toLowerCase() === (first.itemName || '').toLowerCase());
    if (onEditRecipe && menuItem) {
      onEditRecipe(menuItem.menu_item_id);
    } else {
      setError('Edit the recipe from Menu Management, then re-import.');
    }
    setShowMissing(false);
  }, [missingIngredients, miSelected, menuList, onEditRecipe]);

  if (!orderReport) return null;
  const s = orderReport.summary || {};

  // Group missing ingredients by menu item for the popup
  const miGrouped = (missingIngredients || []).reduce((acc, ing) => {
    (acc[ing.itemName] = acc[ing.itemName] || []).push(ing);
    return acc;
  }, {});
  const miVisibleKeys = [];
  const miFiltered = Object.entries(miGrouped).filter(([itemName, ings]) => {
    const q = miSearch.toLowerCase();
    if (!q) return true;
    if (itemName.toLowerCase().includes(q)) return true;
    return ings.some(i => (i.ingredientName || '').toLowerCase().includes(q));
  });
  miFiltered.forEach(([, ings]) => ings.forEach(i => miVisibleKeys.push(miKey(i))));
  const miAllSelected = miVisibleKeys.length > 0 && miVisibleKeys.every(k => miSelected[k]);
  const miSelectedCount = miVisibleKeys.filter(k => miSelected[k]).length;
  const fmtQty = (v) => {
    const n = Number(v) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  };

  return (
    <>
    <div className="sorm-overlay" onClick={onClose}>
      <div className="sorm-container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sorm-head">
          <div>
            <h3 className="sorm-title">
              <span className="sorm-badge" style={{ color: platformColor, borderColor: platformColor }}>{platformLabel}</span>
            </h3>
            <div className="sorm-daterange">
              {s.date_from === s.date_to ? fmtDMY(s.date_from) : `${fmtDMY(s.date_from)} → ${fmtDMY(s.date_to)}`}
            </div>
          </div>
          <button className="sorm-close" onClick={onClose} aria-label="Close"><X size={22} /></button>
        </div>

        {/* Summary chips */}
        <div className="sorm-chips">
          <span className="sorm-chip">{s.order_count || 0} orders</span>
          <span className="sorm-chip">{s.total_items_sold || 0} items</span>
          <span className="sorm-chip">₹{(s.total_revenue || 0).toFixed(0)} gross</span>
          <span className="sorm-chip">{orderReport.matchSummary?.matched || 0} matched · {orderReport.matchSummary?.unmatched || 0} unmatched</span>
          {s.cancelled_count > 0 && <span className="sorm-chip warn">{s.cancelled_count} cancelled</span>}
          {s.truncated_count > 0 && <span className="sorm-chip warn">{s.truncated_count} incomplete</span>}
        </div>

        {error && <div className="sorm-error">{error}</div>}

        {/* Results view (after import) */}
        {results ? (
          <div className="sorm-results">
            <div className="sorm-results-summary">
              <span className="sorm-chip ok">✓ {results.summary.imported} imported</span>
              <span className="sorm-chip">↷ {results.summary.skipped} skipped</span>
              {results.summary.errors > 0 && <span className="sorm-chip warn">⚠ {results.summary.errors} errors</span>}
            </div>
            <div className="sorm-results-list">
              {results.results.map((r, i) => (
                <div key={i} className={`sorm-result-row ${r.status}`}>
                  <span>{fmtDMY(r.order_date)}</span>
                  <span className="sorm-result-status">
                    {r.status === 'imported' ? `✓ ₹${(r.total_amount || 0).toFixed(0)} · COGS ₹${(r.cogs || 0).toFixed(0)}`
                      : r.status === 'skipped' ? (r.reason === 'already_imported' ? '↷ already imported' : '↷ skipped')
                      : `⚠ ${r.reason}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="sorm-footer">
              <div style={{ flex: 1 }} />
              <button className="sorm-submit" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <>
            {/* Per-date list */}
            <div className="sorm-dates">
              {(orderReport.dateSummary || []).map(ds => {
                const isOpen = !!expanded[ds.order_date];
                const dayOrders = orderReport.orders
                  .map((o, idx) => ({ o, idx }))
                  .filter(({ o }) => o.order_date === ds.order_date);
                return (
                  <div key={ds.order_date} className={`sorm-date ${ds.already_imported ? 'imported' : ''}`}>
                    <div className="sorm-date-head" onClick={() => setExpanded(p => ({ ...p, [ds.order_date]: !p[ds.order_date] }))}>
                      <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s', flexShrink: 0 }} />
                      <span className="sorm-date-label">{fmtDMY(ds.order_date)}</span>
                      <span className="sorm-date-meta">{ds.order_count} orders · {ds.item_count} items · ₹{ds.gross.toFixed(0)}</span>
                      {ds.already_imported
                        ? <span className="sorm-status mismatch">Already imported</span>
                        : <span className="sorm-status matched">New</span>}
                    </div>

                    {isOpen && (
                      <div className="sorm-date-body">
                        {dayOrders.map(({ o, idx }) => (
                          <div key={idx} className={`sorm-order ${o.is_cancelled ? 'cancelled' : ''} ${o.is_truncated ? 'truncated' : ''}`}>
                            <div className="sorm-order-head">
                              <span>{o.order_time || '--:--'}</span>
                              <span className="sorm-order-bill-amt">₹{(o.bill_amount || 0).toFixed(0)}</span>
                              {o.is_cancelled && <span className="sorm-status mismatch">Cancelled</span>}
                              {o.is_truncated && <span className="sorm-status unmatched">⚠ {o.missing_count || '?'} item(s) / ₹{(o.missing_amount || 0).toFixed(0)} missing</span>}
                            </div>
                            {o.items.map((it, ii) => {
                              const editable = it.is_matched && !o.is_cancelled && !ds.already_imported;
                              return (
                                <div key={ii} className="sorm-item">
                                  <span className="sorm-item-number">{ii + 1}.</span>
                                  <span className="sorm-item-name">
                                    {it.item_name}{it.variant ? ` (${it.variant})` : ''}
                                    {it.is_matched && it.matched_name && it.matched_name.toLowerCase() !== it.item_name.toLowerCase() &&
                                      <span className="sorm-item-match"> → {it.matched_name}</span>}
                                    {!it.is_matched && <span className="sorm-item-unmatched"> · not in menu</span>}
                                  </span>
                                  <span className="sorm-item-qty">×{it.quantity}</span>
                                  {editable ? (
                                    <span className="sorm-item-price-edit">
                                      ₹<input
                                        type="number"
                                        className="sorm-item-price-input"
                                        value={priceOverrides[`${idx}:${ii}`] !== undefined ? priceOverrides[`${idx}:${ii}`] : (it.true_selling_price || 0)}
                                        onChange={(e) => setPriceOverrides(p => ({ ...p, [`${idx}:${ii}`]: e.target.value }))}
                                      />
                                    </span>
                                  ) : (
                                    <span className="sorm-item-price">₹{(it.true_selling_price || 0).toFixed(0)}</span>
                                  )}
                                </div>
                              );
                            })}
                            {(manualAdds[idx] || []).map((m, mi) => (
                              <div key={`m${mi}`} className="sorm-item manual">
                                <span className="sorm-item-number">{o.items.length + mi + 1}.</span>
                                <span className="sorm-item-name">{m.item_name} <span className="sorm-item-match">· added</span></span>
                                <span className="sorm-item-qty">×{m.quantity}</span>
                                <span className="sorm-item-price">₹{Number(m.price).toFixed(0)}</span>
                                <button className="sorm-item-remove" onClick={() => removeManualItem(idx, mi)} title="Remove">×</button>
                              </div>
                            ))}
                            {o.is_truncated && !o.is_cancelled && !ds.already_imported && (
                              <ManualAddRow menuList={menuList} onAdd={(entry) => addManualItem(idx, entry)} />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Unmatched items (imported as Not in Menu) */}
            {unmatchedList.length > 0 && (
              <div className="sorm-section">
                <div className="sorm-section-head" onClick={() => setShowUnmatched(v => !v)}>
                  <ChevronDown size={15} style={{ transform: showUnmatched ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
                  <span className="sorm-section-title">Unmatched items ({unmatchedList.length})</span>
                  <span className="sorm-section-note">not in menu · saved as Not in Menu</span>
                </div>
                {showUnmatched && (
                  <div className="sorm-section-body">
                    {unmatchedList.map((u, i) => (
                      <div key={i} className="sorm-list-row">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#000' }}>
                          <span className="sorm-item-number">{i + 1}.</span>
                          <span>{u.name}</span>
                        </div>
                        <span className="sorm-list-qty">×{u.qty}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cancelled orders (excluded from revenue + deduction) */}
            {cancelledOrders.length > 0 && (
              <div className="sorm-section">
                <div className="sorm-section-head" onClick={() => setShowCancelled(v => !v)}>
                  <ChevronDown size={15} style={{ transform: showCancelled ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
                  <span className="sorm-section-title">Cancelled orders ({cancelledOrders.length})</span>
                  <span className="sorm-section-note">excluded</span>
                </div>
                {showCancelled && (
                  <div className="sorm-section-body">
                    {cancelledOrders.map((o, i) => (
                      <div key={i} className="sorm-cancel-row">
                        <div className="sorm-cancel-top">
                          <span>{fmtDMY(o.order_date)}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{o.order_time || '--:--'}</span>
                          <span className="sorm-cancel-bill-amt">₹{(o.bill_amount || 0).toFixed(0)}</span>
                        </div>
                        <div className="sorm-cancel-items" style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                          {o.items.map((it, ii) => (
                            <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#000' }}>
                              <span className="sorm-dot-black" />
                              <span>
                                {it.item_name}{it.variant ? ` (${it.variant})` : ''}
                                <span style={{ color: '#6b7280', fontSize: '11.5px', marginLeft: '6px' }}>×{it.quantity}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                        {o.cancel_reason && <div className="sorm-cancel-reason">{o.cancel_reason}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="sorm-footer">
              <label className="sorm-neg">
                <input type="checkbox" checked={allowNegativeStock} onChange={e => setAllowNegativeStock(e.target.checked)} />
                Allow negative stock
              </label>
              <div style={{ flex: 1 }} />
              <div className="sorm-footer-actions">
                <button className="sorm-cancel" onClick={onClose}>Cancel</button>
                <button className="sorm-submit" onClick={handleSubmit} disabled={submitting || checking || newDatesCount === 0}>
                  {checking ? 'Checking stock…' : submitting ? 'Confirming…' : 'Confirm'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Shared datalist for manual completion */}
        <datalist id="sorm-menu-list">
          {menuList.map(m => <option key={m.menu_item_id} value={m.name} />)}
        </datalist>
      </div>
    </div>

    {/* ============ MISSING INGREDIENTS / NEGATIVE STOCK POPUP ============ */}
    {showMissing && (
      <div className="sormi-overlay" onClick={() => setShowMissing(false)}>
        <div className="sormi-container" onClick={e => e.stopPropagation()}>
          <div className="sormi-header">
            <h3 className="sormi-title">Missing Ingredients</h3>
            <button className="sormi-close" onClick={() => setShowMissing(false)}>✕</button>
          </div>

          <div className="sormi-body">
            <p className="sormi-desc">The following ingredients are not available in stock. Please select how to handle each item:</p>

            <div className="sormi-searchrow">
              <input
                type="text"
                className="sormi-search"
                placeholder="Search items…"
                value={miSearch}
                onChange={e => setMiSearch(e.target.value)}
              />
              <button
                className="sormi-selectall"
                onClick={() => {
                  const updated = { ...miSelected };
                  miVisibleKeys.forEach(k => { updated[k] = !miAllSelected; });
                  setMiSelected(updated);
                }}
              >
                {miAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {miFiltered.length === 0 ? (
              <div className="sormi-empty">No items match your search.</div>
            ) : (
              miFiltered.map(([itemName, ings], gi) => {
                const groupAll = ings.every(i => miSelected[miKey(i)]);
                return (
                  <div key={itemName} className="sormi-group">
                    <div className="sormi-group-head">
                      <div className="sormi-group-title">{gi + 1}. {itemName}</div>
                      <button
                        className="sormi-selectall sormi-selectall-sm"
                        onClick={() => {
                          const updated = { ...miSelected };
                          ings.forEach(i => { updated[miKey(i)] = !groupAll; });
                          setMiSelected(updated);
                        }}
                      >
                        {groupAll ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="sormi-list">
                      {ings.map((ing, ii) => {
                        const k = miKey(ing);
                        return (
                          <div key={ii} className="sormi-row">
                            <div className="sormi-row-info">
                              <div className="sormi-ing-name">
                                {ing.ingredientName}
                                {ing.isPreparationComponent && ing.preparationName && (
                                  <span className="sormi-ing-prep"> · in {ing.preparationName}</span>
                                )}
                              </div>
                              <div className="sormi-ing-detail">
                                Required: {fmtQty(ing.required)} {ing.unit} | Available: {fmtQty(ing.available)} {ing.availableUnit || ing.unit}
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              className="sormi-check"
                              checked={Boolean(miSelected[k])}
                              onChange={() => setMiSelected(prev => ({ ...prev, [k]: !prev[k] }))}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {error && <div className="sorm-error" style={{ margin: '0 20px 12px' }}>{error}</div>}

          <div className="sormi-footer">
            <button
              className="sormi-btn sormi-btn-negative"
              onClick={handleConfirmNegativeStock}
              disabled={submitting || miSelectedCount === 0}
            >
              {submitting ? 'Processing…' : 'Confirm & Create Negative Stock'}
            </button>
            <button
              className="sormi-btn sormi-btn-remove"
              onClick={handleRemoveFromRecipe}
              disabled={submitting || miSelectedCount === 0}
            >
              Remove and Update Recipe
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
